import React from "react";
import { cn } from "@/lib/utils";

// `?? ""` matters: this runs at module scope, so a missing VITE_API_URL used to
// throw on import and blank the whole app rather than degrade one icon. An empty
// base yields a relative /api/... path, which is what the dev proxy expects.
const API_URL = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");

interface DomainFaviconProps {
  name: string;
  url?: string | null;
  size?: number;
  className?: string;
}

const getInitials = (name: string): string => {
  const parts = name.split(" ").filter(Boolean);
  if (parts.length === 0) {
    return "--";
  }

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const getHost = (rawUrl?: string | null): string => {
  const trimmedUrl = rawUrl?.trim();
  if (!trimmedUrl) {
    return "";
  }

  try {
    const normalizedUrl = /^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(trimmedUrl)
      ? trimmedUrl
      : `https://${trimmedUrl}`;
    return new URL(normalizedUrl).hostname;
  } catch {
    return trimmedUrl
      .replace(/^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//, "")
      .split("/")[0];
  }
};

const DomainFavicon: React.FC<DomainFaviconProps> = ({
  name,
  url,
  size = 32,
  className,
}) => {
  const [loaded, setLoaded] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  const host = getHost(url);
  const initials = getInitials(name);

  React.useEffect(() => {
    setLoaded(false);
    setFailed(false);
  }, [host]);
  const faviconSrc = `${API_URL}/api/system/favicon/?host=${encodeURIComponent(host)}`;

  if (failed || !host) {
    return (
      <span
        className={cn(
          "flex shrink-0 items-center justify-center rounded-md bg-vb-bg-subtle text-xs font-semibold text-vb-text-2",
          className,
        )}
        style={{ width: size, height: size }}
        aria-hidden="true"
      >
        {initials}
      </span>
    );
  }

  return (
    <span
      className={cn("relative flex shrink-0", className)}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <span
        className="flex h-full w-full items-center justify-center rounded-md bg-vb-bg-subtle text-xs font-semibold text-vb-text-2"
      >
        {initials}
      </span>
      <img
        src={faviconSrc}
        alt=""
        aria-hidden="true"
        width={size}
        height={size}
        referrerPolicy="no-referrer"
        onLoad={() => setLoaded(true)}
        onError={() => setFailed(true)}
        className={cn(
          "absolute inset-0 rounded-md border border-gray-200 bg-white object-cover transition-opacity",
          loaded ? "opacity-100" : "opacity-0",
        )}
      />
    </span>
  );
};

export default DomainFavicon;
